/**
 * formatSkuName + formatSkuPrice — UX-copy dedupe for vendor product
 * titles. Born from the 2026-05-16 audit Lucky flagged where the
 * voucher catalogue was reading absurd on cards:
 *
 *   Endota Spa            ← vendor_name eyebrow
 *   Endota Voucher Premium ← raw name
 *   $200 voucher           ← formatPrice appended " voucher"
 *
 * "Endota" appears twice, "Voucher" / "voucher" appears twice. Same
 * for "Studio Class Voucher / $69 voucher" on Paint Juicy, "Spa Day
 * Voucher / $199 voucher" on Peninsula, etc.
 *
 * The fix is render-time, not seed-time. Seed data stays canonical
 * (vendors and admins read those names in the bakery portal); the
 * customer-facing cards transform them into clean titles. Lucky can
 * re-seed any time without us having to maintain a parallel
 * "display_name" column.
 *
 * What gets stripped from the title, in order:
 *   1. Vendor name prefix — "Endota Voucher Premium" → "Voucher Premium"
 *      when vendor_name starts with "Endota". Only strips at the
 *      START of the name (so a SKU called "Belgian Chocolate Cake" at
 *      "Belgian Bakery" isn't gutted — "Belgian" mid-phrase stays).
 *   2. Dollar-amount prefix — "$50 Class Voucher" → "Class Voucher".
 *      Price is already on its own line; doubling up reads like an
 *      error.
 *   3. Trailing " Voucher" — "Class Voucher" → "Class" when
 *      is_voucher=true. The price-side " voucher" suffix (via
 *      formatSkuPrice below) is the single source of truth for the
 *      voucher signal.
 *   4. Standalone "Voucher" anywhere else — "Voucher Entry" → "Entry"
 *      catches the awkward middle-position cases like
 *      "Bathing Voucher Standard" → "Bathing Standard".
 *
 * If stripping leaves an empty string (e.g. the SKU was literally
 * just "Voucher"), we fall back to a Title-Cased version of
 * vendor_category so the card never renders without a title.
 *
 * Safe to call on every SKU including non-vouchers — the voucher-
 * specific strips only fire when is_voucher=true.
 */

interface FormatSkuNameInput {
  name: string;
  vendor_name?: string;
  vendor_category?: string;
  is_voucher?: boolean;
}

/** Title-cases a single token: "spa" → "Spa", "WORKSHOP" → "Workshop". */
function titleCase(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Return the customer-facing title for an SKU, with redundant
 * vendor / price / "voucher" tokens stripped. Idempotent and safe
 * to call repeatedly.
 */
export function formatSkuName(input: FormatSkuNameInput): string {
  let out = (input.name ?? "").trim();
  if (!out) return titleCase(input.vendor_category ?? "Gift");

  // 1. Vendor name prefix strip. Try the full vendor name first so
  //    "Sense of Self Gift Voucher $100" loses "Sense of Self" entirely
  //    rather than just "Sense", which left the broken "of Self Gift …"
  //    prefix. Fall back to the first word only for vendors whose name
  //    genuinely appears as a partial prefix in the product title (e.g.
  //    "Hepburn Bathhouse Gift Certificate" under "Hepburn Bathhouse & Spa"
  //    — the vendor name doesn't match verbatim, so we strip "Hepburn").
  const vendorNameFull = (input.vendor_name ?? "").trim();
  if (vendorNameFull) {
    const reFullVendor = new RegExp(
      `^${escapeForRegex(vendorNameFull)}\\s*`,
      "i",
    );
    if (reFullVendor.test(out)) {
      out = out.replace(reFullVendor, "").trim();
    } else {
      // First-word fallback — keeps "Hepburn Bathhouse Gift Certificate"
      // from retaining "Hepburn" while avoiding false-positive strips on
      // names like "Belgian Chocolate Cake" at "Belgian Bakery".
      const vendorFirst = vendorNameFull.split(/\s+/)[0];
      const re = new RegExp(`^${escapeForRegex(vendorFirst)}\\s+`, "i");
      out = out.replace(re, "").trim();
    }
  }

  // 2. Dollar-amount prefix strip ("$50 Class Voucher" → "Class Voucher").
  //    Handles both "$50" and "$50 " spellings.
  out = out.replace(/^\$\d+(?:\.\d+)?\s+/, "").trim();

  // 2b. Dollar-amount SUFFIX strip ("Gift Certificate $100" → "Gift
  //     Certificate"). The price line already shows the denomination via
  //     formatSkuPrice, so embedding it in the name is redundant — and
  //     the redundancy is what Lucky flagged for Hepburn & Sense of Self
  //     gift-certificate tiers.
  out = out.replace(/\s+\$\d+(?:\.\d+)?$/, "").trim();

  // 3 + 4. Voucher-token strips, only when the SKU is actually a
  //    voucher. The price formatter below carries the voucher signal
  //    on its own line, so duplicating "Voucher" in the title is
  //    noise.
  if (input.is_voucher) {
    // Trailing " Voucher" or "Voucher" alone at the end.
    out = out.replace(/\s+Voucher\b/i, "").trim();
    out = out.replace(/^Voucher\b/i, "").trim();
    // Standalone "Voucher" mid-phrase ("Bathing Voucher Standard"
    // → "Bathing Standard"). Matches a Voucher token surrounded by
    // word boundaries — won't touch words like "Vouchered" if any
    // ever appear (unlikely but defensive).
    out = out.replace(/\bVoucher\b/i, "").replace(/\s{2,}/g, " ").trim();
  }

  // Fallback: if all that stripping left us with nothing, surface
  // the category title-cased. Never render an empty heading.
  if (!out) return titleCase(input.vendor_category ?? "Gift");

  // MS490: dollar-value gift vouchers are often seeded as
  // "{Vendor} Gift Voucher $100" — after vendor + $ + "Voucher"
  // strips the title collapses to bare "Gift" or "Class", which
  // reads broken on cards. When that happens, infer a category-
  // aware label until the canonical name is migrated.
  if (input.is_voucher && isWeakVoucherTitle(out)) {
    return voucherTitleFallback(input.vendor_category);
  }

  return out;
}

/** True when the stripped title is too generic to stand alone on a card. */
function isWeakVoucherTitle(title: string): boolean {
  const t = title.trim().toLowerCase();
  return t === "gift" || t === "class" || t === "card";
}

/** Category-aware voucher title when canonical name strips to noise. */
function voucherTitleFallback(vendorCategory?: string): string {
  switch ((vendorCategory ?? "").toLowerCase()) {
    case "spa":
      return "Bathhouse Gift Card";
    case "experience":
      return "Experience Gift Card";
    case "workshop":
      return "Workshop Gift Card";
    case "hamper":
      return "Gift Hamper";
    case "coffee":
      return "Coffee Gift Card";
    case "florist":
      return "Florist Gift Card";
    default:
      return "Gift Card";
  }
}

/**
 * Price formatter. When is_voucher AND the cleaned name already
 * mentions "voucher" (which the audit found was the failure mode),
 * we still append " voucher" on the price line because the title
 * strip removed that signal up there. The result: "voucher" appears
 * exactly ONCE per card — on the price line — instead of in the
 * title AND the price line. When the SKU is NOT a voucher, just
 * the dollar amount.
 */
export function formatSkuPrice(
  priceAud: number,
  isVoucher: boolean,
  options?: { prefixFrom?: boolean },
): string {
  const value = `$${priceAud.toFixed(0)}`;
  const lead = options?.prefixFrom ? "From " : "";
  return isVoucher ? `${lead}${value} voucher` : `${lead}${value}`;
}

function escapeForRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
